// ImageCropModal — the "adjust your picture" step (#249).
// Shown after a user picks a file, before uploading: a round crop with zoom +
// drag-to-reposition. Confirming produces a square WEBP Blob (the visible circle's
// bounding box) which the caller uploads. Built on react-easy-crop.
import React, { useCallback, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { ZoomIn, ZoomOut } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { ActionButton, OutlineButton } from './action-button';

const OUTPUT_MAX = 512; // px — cap the stored avatar; plenty for any avatar render

// Draw the chosen crop rectangle to a square canvas and export a WEBP blob.
const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => resolve(img));
        img.addEventListener('error', reject);
        img.src = src;
    });

const getCroppedBlob = async (imageSrc: string, area: Area): Promise<Blob> => {
    const image = await loadImage(imageSrc);
    const size = Math.min(Math.round(area.width), OUTPUT_MAX);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unsupported');
    ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/webp', 0.9);
    });
};

interface ImageCropModalProps {
    open: boolean;
    imageSrc: string | null;
    loading?: boolean;
    onCancel: () => void;
    onConfirm: (blob: Blob) => void | Promise<void>;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({ open, imageSrc, loading, onCancel, onConfirm }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [areaPixels, setAreaPixels] = useState<Area | null>(null);

    const onCropComplete = useCallback((_area: Area, pixels: Area) => setAreaPixels(pixels), []);

    // Reset transform each time a new image opens so it starts centered at 1×.
    const resetView = useCallback(() => { setCrop({ x: 0, y: 0 }); setZoom(1); setAreaPixels(null); }, []);

    const handleConfirm = async () => {
        if (!imageSrc || !areaPixels) return;
        const blob = await getCroppedBlob(imageSrc, areaPixels);
        await onConfirm(blob);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !loading) onCancel(); }}>
            <DialogContent className="sm:max-w-md" onOpenAutoFocus={resetView}>
                <DialogHeader>
                    <DialogTitle>Justera profilbild</DialogTitle>
                </DialogHeader>

                {/* Crop stage — square, dark backdrop, circular mask via cropShape. */}
                <div className="relative w-full h-72 sm:h-80 rounded-xl overflow-hidden bg-gray-900">
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            restrictPosition
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    )}
                </div>

                {/* Zoom control */}
                <div className="flex items-center gap-3 px-1">
                    <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <Slider
                        value={[zoom]}
                        min={1}
                        max={3}
                        step={0.01}
                        onValueChange={(v) => setZoom(v[0])}
                        aria-label="Zooma"
                    />
                    <ZoomIn className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
                <p className="text-xs text-gray-400 px-1">Dra bilden för att flytta den, zooma med reglaget.</p>

                <DialogFooter className="gap-2 sm:gap-2">
                    <OutlineButton onClick={onCancel} disabled={loading}>Avbryt</OutlineButton>
                    <ActionButton onClick={handleConfirm} loading={loading} disabled={loading || !areaPixels}>
                        Använd bild
                    </ActionButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ImageCropModal;
